import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import SignOutConfirmModal from './SignOutConfirmModal';

interface UserBottomMenuProps {
  activeTab?: 'dashboard' | 'appointments' | 'work' | 'favorites' | 'business-exploration' | 'profile';
  onTabPress?: (tab: 'dashboard' | 'appointments' | 'work' | 'favorites' | 'business-exploration' | 'profile') => void;
  showWorkTab?: boolean;
  onLogin?: () => void;
  onCreateAccount?: () => void;
  onHelp?: () => void;
  onLogout?: () => void;
  showAuthButtons?: boolean;
}

const UserBottomMenu: React.FC<UserBottomMenuProps> = ({
  activeTab,
  onTabPress,
  showWorkTab = false,
  onLogin,
  onCreateAccount,
  onHelp,
  onLogout,
  showAuthButtons = false,
}) => {
  const [showSignOutModal, setShowSignOutModal] = useState(false);

  const baseMenuItems = [
    {
      id: 'dashboard' as const,
      label: 'Home',
      icon: '⌂',
      description: 'Dashboard',
    },
    {
      id: 'appointments' as const,
      label: 'Bookings',
      icon: '✓',
      description: 'My Appointments',
    },
  ] as const;

  const workMenuItem = {
    id: 'work' as const,
    label: 'Work',
    icon: '🏢',
    description: 'Work Appointments',
  };

  const tailMenuItems = [
    {
      id: 'favorites' as const,
      label: 'Favorites',
      icon: '♥',
      description: 'Saved Businesses',
    },
    {
      id: 'profile' as const,
      label: 'Settings',
      icon: '⋮',
      description: 'Account & Plans',
    },
    {
      id: 'logout',
      label: 'Sign out',
      icon: '→',
      description: 'Sign Out',
    },
  ] as const;

  const menuItems = [
    ...baseMenuItems,
    ...(showWorkTab ? [workMenuItem] : []),
    ...tailMenuItems,
  ];

  const authButtons = [
    {
      id: 'login',
      label: 'Login',
      icon: '🔑',
      description: 'Sign In',
    },
    {
      id: 'create-account',
      label: 'Create Account',
      icon: '👤',
      description: 'Sign Up',
    },
    {
      id: 'help',
      label: 'Help',
      icon: '?',
      description: 'Support',
    },
  ];

  const handleButtonPress = (buttonId: string) => {
    switch (buttonId) {
      case 'login':
        onLogin?.();
        break;
      case 'create-account':
        onCreateAccount?.();
        break;
      case 'help':
        onHelp?.();
        break;
      case 'logout':
        setShowSignOutModal(true);
        break;
      default:
        if (onTabPress) {
          onTabPress(buttonId as any);
        }
    }
  };

  const buttonsToShow = showAuthButtons ? authButtons : menuItems;

  return (
    <>
      <SignOutConfirmModal
        visible={showSignOutModal}
        onCancel={() => setShowSignOutModal(false)}
        onConfirm={() => {
          setShowSignOutModal(false);
          onLogout?.();
        }}
      />
      <View 
        style={styles.container}
      >
        <View style={styles.menuBar}>
        {buttonsToShow.map((item) => (
          <TouchableOpacity
            key={item.id}
            style={[
              styles.menuItem,
              !showAuthButtons && activeTab === item.id && styles.activeMenuItem,
            ]}
            onPress={() => handleButtonPress(item.id)}
          >
            <Text
              style={[
                styles.menuIcon,
                !showAuthButtons && activeTab === item.id && styles.activeMenuIcon,
              ]}
            >
              {item.icon}
            </Text>
            <Text
              style={[
                styles.menuLabel,
                !showAuthButtons && activeTab === item.id && styles.activeMenuLabel,
              ]}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E7',
    ...(Platform.OS === 'web' ? {
      position: 'fixed' as any, // Fixed position for web
    } : {
      position: 'absolute', // Absolute for mobile
    }),
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    width: Platform.OS === 'web' ? '100%' : undefined, // Ensure full width on web
  },
  menuBar: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 4,
    paddingVertical: 2,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  menuItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderRadius: 8,
    marginHorizontal: 2,
    backgroundColor: 'transparent',
  },
  activeMenuItem: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#E5E5E7',
  },
  menuIcon: {
    fontSize: 22,
    marginBottom: 4,
    color: '#666666',
    ...(Platform.OS === 'web' ? {
      WebkitFontSmoothing: 'antialiased' as any,
      MozOsxFontSmoothing: 'grayscale' as any,
    } : {}),
  },
  activeMenuIcon: {
    color: '#007AFF',
  },
  menuLabel: {
    fontSize: 11,
    fontWeight: '500',
    color: '#666666',
    marginTop: 2,
    ...(Platform.OS === 'web' ? {
      WebkitFontSmoothing: 'antialiased' as any,
      MozOsxFontSmoothing: 'grayscale' as any,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    } : {}),
  },
  activeMenuLabel: {
    color: '#007AFF',
    fontWeight: '600',
  },
});

export default UserBottomMenu;
